/**
 * Ranking-eval data layer: TanStack-backed reads plus the module's mutations,
 * the client interface for the Ranking Evaluation tooling.
 *
 * The read hooks hide the TanStack machinery. The mutations are plain
 * `Datastore`-taking async functions (call sites fire them from callbacks,
 * not React render) that invalidate the affected read query on success.
 */
import type {
    EvalCommentRating,
    EvalComparison,
    EvalConversationSet,
    EvalCustomRanker,
    EvalDebugInfo,
    ExternalEvalSpace,
    RankerDetail,
    RankerSummary,
} from '@repo/shared/orpc/procedures/ranking-eval';
import { type CommentTreeNode } from 'data/comment';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore, useInstanceKey } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { siloParamsOf } from './silo-params';
import { useSiloQuery } from './use-silo-query';

export type {
    EvalCommentRating,
    EvalComparison,
    EvalConversationSet,
    EvalCustomRanker,
    EvalDebugInfo,
    ExternalEvalSpace,
    RankerDetail,
    RankerSummary,
} from '@repo/shared/orpc/procedures/ranking-eval';

// ============================================================================
// Reads
// ============================================================================

/** All conversations fetched into the eval tool, keyed by conversation key. */
export function useImportableEvalConversations(): Record<string, ExternalEvalSpace> {
    const utils = useApiQueryUtils();
    const { data } = useSiloQuery({ procedure: utils.rankingEval.listConversations });
    return Object.fromEntries((data ?? []).map((conversation) => [conversation.key, conversation]));
}

/**
 * The current conversation's stored name and import time. `name` is `null`
 * until an import has run — callers fall back to the instance key. `importedAt`
 * is `null` until the first import and drives the import gate. `isPending` is
 * true only on the first load, before either value is known.
 */
export function useEvalConversation(): { name: string | null; importedAt: string | null; isPending: boolean } {
    const instanceKey = useInstanceKey();
    const utils = useApiQueryUtils();
    const { data, isPending } = useSiloQuery({ procedure: utils.rankingEval.getConversation, input: { instanceKey } });
    return { name: data?.name ?? null, importedAt: data?.importedAt ?? null, isPending };
}

/** The shared selected conversation set, or `null` if none has been saved. */
export function useSelectedEvalConversationSet(): EvalConversationSet | null {
    const utils = useApiQueryUtils();
    const { data } = useSiloQuery({ procedure: utils.rankingEval.getSelectedSet });
    return data ?? null;
}

/** Every custom ranker config. */
export function useEvalCustomRankers(): EvalCustomRanker[] {
    const utils = useApiQueryUtils();
    const { data } = useSiloQuery({ procedure: utils.rankingEval.listCustomRankers });
    return data ?? [];
}

/** Every built-in ranker available for comparison. */
export function useEvalRankerList(): RankerSummary[] {
    const utils = useApiQueryUtils();
    const { data } = useSiloQuery({ procedure: utils.rankingEval.getAvailableRankers });
    return data ?? [];
}

/** A built-in ranker with prompts (`undefined` while loading/unknown). */
export function useEvalRankerDetails(rankerKey: string): RankerDetail | undefined {
    const utils = useApiQueryUtils();
    const { data } = useSiloQuery({ procedure: utils.rankingEval.getRankerDetails, input: { rankerKey } });
    return data;
}

/** Comparison votes for the current conversation, keyed by vote key. */
export function useEvalComparisons(): Record<string, EvalComparison> {
    const instanceKey = useInstanceKey();
    const utils = useApiQueryUtils();
    const { data } = useSiloQuery({ procedure: utils.rankingEval.listComparisons, input: { instanceKey } });
    return Object.fromEntries((data ?? []).map((comparison) => [comparison.key, comparison]));
}

/**
 * Comment ratings for the current conversation instance, keyed by rating key
 * (`{userId}-{commentKey}`), plus a `refresh` that refetches them so a fresh
 * submission shows up.
 */
export function useEvalCommentRatings(): { ratings: Record<string, EvalCommentRating>; refresh: () => void } {
    const instanceKey = useInstanceKey();
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.rankingEval.getCommentRatings, input: { instanceKey } });
    const ratings = Object.fromEntries((query.data ?? []).map((rating) => [rating.key, rating]));
    const refresh = () => fireAndForget(query.refetch());
    return { ratings, refresh };
}

/**
 * The current conversation's imported comments, ranked by `sort`. Mirrors
 * `useCommentTree`, but reads through the `rankingEval` resource, which does
 * the ranking server-side (the same `resolveCommentTreeAsync`/`rankComments`
 * pipeline `comments.list` uses for real spaces).
 *
 * `sort: null` disables the query — used while an LLM ranker's scoring is
 * still in progress, since requesting a ranking before it's scored throws
 * `NOT_SCORED` (see `side-by-side.tsx`'s `useLLMRanking`).
 */
export function useEvalConversationComments(sort: string | null): { tree: CommentTreeNode[]; isPending: boolean; error: Error | null } {
    const instanceKey = useInstanceKey();
    const utils = useApiQueryUtils();
    const { data, isPending, error } = useSiloQuery({
        procedure: utils.rankingEval.listConversationComments,
        input: { instanceKey, sort: sort ?? '' },
        enabled: sort !== null,
    });
    return { tree: data?.items ?? [], isPending, error: error ?? null };
}

/**
 * Per-comment first-pass scores/reasoning and second-pass reasoning for a
 * ranker on the current conversation — debug-only data for the Side-by-Side
 * tab's debug overlay. `rankerKey: null` disables the query (non-LLM rankers
 * have no scores, and the overlay is usually hidden).
 */
export function useEvalDebugInfo(rankerKey: string | null): EvalDebugInfo | undefined {
    const instanceKey = useInstanceKey();
    const utils = useApiQueryUtils();
    const { data } = useSiloQuery({
        procedure: utils.rankingEval.getDebugInfo,
        input: { instanceKey, rankerKey: rankerKey ?? '' },
        enabled: rankerKey !== null,
    });
    return data;
}

// ============================================================================
// Mutations
// ============================================================================

function invalidate(datastore: Datastore, queryKey: readonly unknown[]) {
    fireAndForget(datastore.getQueryClient().invalidateQueries({ queryKey }));
}

/** Re-fetch conversation lists from all known sites; invalidates the list. */
export async function refreshEvalConversationsAsync(datastore: Datastore): Promise<ExternalEvalSpace[]> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const result = await datastore.getApiClient().rankingEval.refreshConversations({ siloKey, locale });
    invalidate(datastore, datastore.getApiQueryUtils().rankingEval.listConversations.key({ input: { siloKey } }));
    return result;
}

/** Replace the shared selected conversation set; invalidates it. */
export async function updateSelectedEvalConversationSetAsync(
    datastore: Datastore,
    { conversationKeys, conversationNames }: { conversationKeys: string[]; conversationNames: Record<string, string> },
): Promise<EvalConversationSet> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const result = await datastore.getApiClient().rankingEval.updateSelectedSet({ siloKey, locale, conversationKeys, conversationNames });
    invalidate(datastore, datastore.getApiQueryUtils().rankingEval.getSelectedSet.key({ input: { siloKey } }));
    return result;
}

/** Create or update a custom ranker; invalidates the ranker list. */
export async function saveEvalCustomRankerAsync(
    datastore: Datastore,
    { ranker }: { ranker: EvalCustomRankerInput },
): Promise<EvalCustomRanker> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const result = await datastore.getApiClient().rankingEval.saveCustomRanker({ siloKey, locale, ranker });
    invalidate(datastore, datastore.getApiQueryUtils().rankingEval.listCustomRankers.key({ input: { siloKey } }));
    return result;
}

/** Delete a custom ranker; invalidates the ranker list. */
export async function deleteEvalCustomRankerAsync(datastore: Datastore, { rankerKey }: { rankerKey: string }): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    await datastore.getApiClient().rankingEval.deleteCustomRanker({ siloKey, locale, rankerKey });
    invalidate(datastore, datastore.getApiQueryUtils().rankingEval.listCustomRankers.key({ input: { siloKey } }));
}

/**
 * Import the current conversation's comments and personas into the instance.
 * Invalidates the imported-comments query so the freshly imported comments
 * show up, plus the conversation read so its name and import status refresh.
 */
export async function importEvalConversationAsync(
    datastore: Datastore,
    { force }: { force?: boolean } = {},
): Promise<{ commentCount: number; personaCount: number }> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const instanceKey = datastore.getInstanceKey();
    const result = await datastore.getApiClient().rankingEval.importConversation({ siloKey, locale, instanceKey, force });
    invalidate(datastore, datastore.getApiQueryUtils().rankingEval.listConversationComments.key({ input: { siloKey, instanceKey } }));
    invalidate(datastore, datastore.getApiQueryUtils().rankingEval.getConversation.key({ input: { siloKey, instanceKey } }));
    return result;
}

/** Score one batch of unscored comments for a ranker. */
export async function runEvalFirstPassAsync(
    datastore: Datastore,
    { rankerKey }: { rankerKey: string },
): Promise<{ status: string; scored: number; total: number; remaining: number }> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const instanceKey = datastore.getInstanceKey();
    return datastore.getApiClient().rankingEval.runFirstPass({ siloKey, locale, instanceKey, rankerKey });
}

/** Ranks the top-scored comments for a ranker on the current conversation. */
export async function runEvalSecondPassAsync(
    datastore: Datastore,
    { rankerKey }: { rankerKey: string },
): Promise<{ status: string; cached?: boolean }> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const instanceKey = datastore.getInstanceKey();
    return datastore.getApiClient().rankingEval.runSecondPass({ siloKey, locale, instanceKey, rankerKey });
}

/** Submit a side-by-side comparison vote for the current conversation. */
export async function submitEvalComparisonAsync(
    datastore: Datastore,
    { rankerA, rankerB, winner }: { rankerA: string; rankerB: string; winner: EvalComparison['winner'] },
): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const instanceKey = datastore.getInstanceKey();
    await datastore.getApiClient().rankingEval.submitComparison({ siloKey, locale, instanceKey, rankerA, rankerB, winner });
    invalidate(datastore, datastore.getApiQueryUtils().rankingEval.listComparisons.key({ input: { siloKey, instanceKey } }));
}

/** Submit a 1-5 rating for a comment in the current conversation. */
export async function submitEvalCommentRatingAsync(
    datastore: Datastore,
    { commentKey, rating, reason }: { commentKey: string; rating: number; reason?: string },
): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const instanceKey = datastore.getInstanceKey();
    await datastore.getApiClient().rankingEval.submitCommentRating({ siloKey, locale, instanceKey, commentKey, rating, reason });
    invalidate(datastore, datastore.getApiQueryUtils().rankingEval.getCommentRatings.key({ input: { siloKey, instanceKey } }));
}

/** The custom-ranker payload sent on save (`key` absent when creating one). */
type EvalCustomRankerInput = Omit<EvalCustomRanker, 'key'> & { key?: string };
